import Head from 'next/head'
import { Fragment, type FC } from 'react'
import { StructuredText, type RenderBlockContext } from 'react-datocms'
import styled from 'styled-components'

import type { OurStudiesPageRecord, OutStudiesPageContentBlock } from '@/graphql/OurStudies'

import { CentredContentBlock } from '@/components/blocks/CentredContentBlock'
import { SciencePagePredictStudiesBlock } from '@/components/blocks/SciencePagePredictStudiesBlock'
import { SciencePageStudiesGroupBlock } from '@/components/blocks/SciencePageStudiesGroupBlock'
import { Footer } from '@/components/elements/footer/Footer'
import { Header } from '@/components/elements/header/Header'
import { ScienceSubNavigation } from '@/components/elements/science/ScienceSubNavigation'

type OurStudiesPageProps = {
  page: OurStudiesPageRecord
}

const StyledCenteredContentBlock = styled.div`
  h1 {
    margin-bottom: 32px;

    @media screen and (max-width: ${(p) => p.theme.breakpoints.mobile}) {
      font-size: ${(p) => p.theme.v2.typography.display.xxs.fontSize};
      line-height: ${(p) => p.theme.v2.typography.display.xxs.lineHeight};
    }
  }

  p {
    max-width: 760px;
    margin-top: 0;
    margin-bottom: 0;
    font-size: ${(p) => p.theme.v2.typography.body.m.fontSize};
    line-height: ${(p) => p.theme.v2.typography.body.m.lineHeight};
    font-family: ${(p) => p.theme.v2.font.lotaGrotesqueLight};
    color: ${(props) => props.theme.v2.onSurface.subduedText};

    @media screen and (max-width: ${(p) => p.theme.breakpoints.mobile}) {
      font-size: ${(p) => p.theme.v2.typography.body.xs.fontSize};
      line-height: ${(p) => p.theme.v2.typography.body.xs.lineHeight};
    }
  }

  a.button {
    margin-top: 40px;
  }
`

const renderBlock = ({ record }: RenderBlockContext<OutStudiesPageContentBlock>) => {
  switch (record.__typename) {
    case 'CentredContentBlockRecord':
      return (
        <Fragment key={record.id}>
          <StyledCenteredContentBlock>
            {/* @ts-expect-error — CentredContentBlock is still typed by hand-written DatoCMS record */}
            <CentredContentBlock record={record} />
          </StyledCenteredContentBlock>

          <SciencePagePredictStudiesBlock />
        </Fragment>
      )

    case 'SciencePageStudiesGroupRecord':
      return <SciencePageStudiesGroupBlock record={record} key={record.id} />

    default:
      record satisfies never
      return null
  }
}

export const OurStudiesPage: FC<OurStudiesPageProps> = ({ page }) => {
  return (
    <>
      <Head>
        <title>Our Studies</title>
        <link rel="canonical" href={`${process.env.NEXT_PUBLIC_SITE_URL}/our-studies`} />
      </Head>

      <Header />

      <ScienceSubNavigation selected="/our-studies" />

      <main id="main-content">
        <StructuredText data={page.content} renderBlock={renderBlock} />
      </main>

      <Footer showPreFooter />
    </>
  )
}
